import { IntegrationsModule } from '@/modules/integrations.module';
import { defineStore } from 'pinia';
import { apiRequestHandler } from '@/utils/api';
import { useOrganizationStore } from './organizationStore';
import {
  ADOListResponse,
  ADOProcessConfiguration,
  ADOTeam,
  ADOTeamProject,
  ADOWorkItemField2,
  ADOWorkItemTag,
  ADOWorkItemTypeFieldInstance,
  ADOWorkType,
  JiraField,
  JiraFieldCreateMetadata,
  JiraIssueType,
  JiraIssueTypeWithStatus,
  JiraLabel,
  JiraPaginatedResponse,
  JiraPriorityScheme,
  JiraProject,
  JiraUser,
} from '@/models/integrations/integrations.models';
import { cloneDeep, orderBy } from 'lodash-es';
import { useJiraProjectEntities } from '@/components/settings/integrations/jira-integration-settings/utils';
import {
  AdoMappings,
  CreateIntegrationInstallation,
  CredentialsAccountsListResponse,
  IntegrationCredential,
  IntegrationInstallation,
  IntegrationJobType,
  IntegrationProvider,
  IntegrationService,
  JiraIntegrationMetadata,
  JiraMappings,
  Job,
  JobsService,
  JobStatus,
  JobType,
  SubmitDisableCredentialIntegrationJob,
  SubmitDisableInstallationIntegrationJob,
  SubmitIntegrationJob,
  UpdateIntegrationCredentialData,
  WorkItemIntegrations,
} from '@/api';

type ADOInstanceEntitiesValue = {
  adoBaseURL: string;
  adoProjects: ADOTeamProject[];
  adoTeams: ADOTeam[];
  adoProjectProcessConfig: Map<ADOTeamProject['id'], ADOProcessConfiguration>;
  adoProjectWorkTypes: Map<ADOTeamProject['id'], ADOWorkType[]>;
  adoProjectTags: Map<ADOTeamProject['id'], ADOWorkItemTag[]>;
  adoProjectFields: Map<ADOTeamProject['id'], ADOWorkItemField2[]>;

  adoProjectWorkTypeFieldMap: Map<
    ADOTeamProject['id'],
    Map<ADOWorkType['name'], ADOWorkItemTypeFieldInstance[]>
  >;

  // adoProjectBoards: Map<ADOTeamProject['id'], ADOWorkBoard[]>;
};

type JiraInstanceEntitiesValue = {
  jiraProjects: JiraProject[];
  jiraIssueTypes: JiraIssueType[];
  jiraLabels: JiraLabel[];
  jiraFields: JiraField[];
  jiraPrioritySchemes: JiraPriorityScheme[];
  jiraUsers: JiraUser[];

  jiraProjectIssueTypeFieldMap: Map<
    JiraProject['id'],
    Map<JiraIssueType['id'], JiraFieldCreateMetadata[]>
  >;
  jiraProjectIssueTypeStatusMap: Map<
    JiraProject['id'],
    JiraIssueTypeWithStatus[]
  >;
};

export interface IntegrationStoreState {
  integrationCredentials: IntegrationCredential[];
  integrationCredentialAccounts: Record<
    IntegrationCredential['id'],
    CredentialsAccountsListResponse['data']
  >;
  integrationInstallations: IntegrationInstallation[];

  integrationsToken: string;

  // jira entities

  _jiraInstanceEntities: Record<
    IntegrationInstallation['id'],
    JiraInstanceEntitiesValue | undefined
  >;

  jiraMappings: Record<IntegrationInstallation['id'], JiraMappings | undefined>;

  jiraJobHistory: {
    isLoading: 'initial' | 'more' | 'refresh' | false;
    cursor: string | undefined;
    hasMoreData: boolean;
    lastUpdated: Date | undefined;
    jobs: Job[];
  };

  // azure devops entities

  _adoInstanceEntities: Record<
    IntegrationInstallation['id'],
    ADOInstanceEntitiesValue | undefined
  >;

  adoMappings: Record<IntegrationInstallation['id'], AdoMappings | undefined>;

  adoJobHistory: {
    isLoading: 'initial' | 'more' | 'refresh' | false;
    cursor: string | undefined;
    hasMoreData: boolean;
    lastUpdated: Date | undefined;
    jobs: Job[];
  };
}

export const useIntegrationsStore = defineStore('integrations', {
  state: (): IntegrationStoreState => ({
    integrationCredentials: [],
    integrationCredentialAccounts: {},
    integrationInstallations: [],

    integrationsToken: '',

    // jira entities
    _jiraInstanceEntities: {},

    jiraMappings: {},

    jiraJobHistory: {
      isLoading: false,
      cursor: undefined,
      hasMoreData: false,
      lastUpdated: undefined,
      jobs: [],
    },

    // azure devops entities
    _adoInstanceEntities: {},

    adoMappings: {},

    adoJobHistory: {
      isLoading: false,
      cursor: undefined,
      hasMoreData: false,
      lastUpdated: undefined,
      jobs: [],
    },
  }),
  getters: {
    connectedJiraInstances(): IntegrationInstallation[] {
      return this.integrationInstallations.filter(
        (installation) => installation.provider === IntegrationProvider.JIRA
      );
    },

    isJiraConnected(): boolean {
      return this.connectedJiraInstances.length > 0;
    },

    connectedADOInstances(): IntegrationInstallation[] {
      return this.integrationInstallations.filter(
        (installation) => installation.provider === IntegrationProvider.ADO
      );
    },

    isADOConnected(): boolean {
      return this.connectedADOInstances.length > 0;
    },

    hasUnfinishedADOJobs(): boolean {
      return this.adoJobHistory.jobs.some(
        (job) =>
          job.status === JobStatus.PENDING || job.status === JobStatus.RUNNING
      );
    },

    hasUnfinishedADODisableJobs(): boolean {
      return this.adoJobHistory.jobs.some((job) => {
        const input =
          job.input as SubmitDisableInstallationIntegrationJob['input'];
        return (
          [
            IntegrationJobType.ADO_DISABLE_PROJECT,
            IntegrationJobType.ADO_DISABLE_INSTALLATION,
            IntegrationJobType.ADO_DISABLE_CREDENTIAL,
          ].includes(input.type) &&
          (job.status === JobStatus.PENDING || job.status === JobStatus.RUNNING)
        );
      });
    },

    hasUnfinishedADOImportJobs(): boolean {
      return this.adoJobHistory.jobs.some((job) => {
        const input = job.input as SubmitIntegrationJob['input'];
        return (
          input.type === IntegrationJobType.ADO_IMPORT &&
          (job.status === JobStatus.PENDING || job.status === JobStatus.RUNNING)
        );
      });
    },

    hasUnfinishedJiraJobs(): boolean {
      return this.jiraJobHistory.jobs.some(
        (job) =>
          job.status === JobStatus.PENDING || job.status === JobStatus.RUNNING
      );
    },

    hasUnfinishedJiraDisableJobs(): boolean {
      return this.jiraJobHistory.jobs.some((job) => {
        const input =
          job.input as SubmitDisableInstallationIntegrationJob['input'];
        return (
          [
            IntegrationJobType.JIRA_DISABLE_PROJECT,
            IntegrationJobType.JIRA_DISABLE_INSTALLATION,
            IntegrationJobType.JIRA_DISABLE_CREDENTIAL,
          ].includes(input.type) &&
          (job.status === JobStatus.PENDING || job.status === JobStatus.RUNNING)
        );
      });
    },

    hasUnfinishedJiraImportJobs(): boolean {
      return this.jiraJobHistory.jobs.some((job) => {
        const input = job.input as SubmitIntegrationJob['input'];
        return (
          input.type === IntegrationJobType.JIRA_IMPORT &&
          (job.status === JobStatus.PENDING || job.status === JobStatus.RUNNING)
        );
      });
    },

    jiraBaseURLs(): Record<
      IntegrationInstallation['id'],
      string
    > /* installationId -> baseURL */ {
      const ret: Record<string, string> = {};
      this.connectedJiraInstances.forEach((installation) => {
        if (installation.provider !== IntegrationProvider.JIRA) return;
        const metadata = installation.metadata as JiraIntegrationMetadata;
        ret[
          installation.id
        ] = `https://${metadata.jiraAccountName}.atlassian.net`;
      });
      return ret;
    },
  },

  actions: {
    /** returns a fresh copy each time so the caller can mutate and use it with _setJiraInstanceEntities */
    _getJiraInstanceEntitiesWithDefaults(
      installationId?: IntegrationInstallation['id']
    ): JiraInstanceEntitiesValue {
      const existing = installationId
        ? this._jiraInstanceEntities[installationId]
        : undefined;

      const defaults: JiraInstanceEntitiesValue = {
        jiraProjects: [],
        jiraIssueTypes: [],
        jiraLabels: [],
        jiraFields: [],
        jiraPrioritySchemes: [],
        jiraUsers: [],
        jiraProjectIssueTypeFieldMap: new Map(),
        jiraProjectIssueTypeStatusMap: new Map(),

        ...(existing ?? {}),
      };

      return cloneDeep(defaults);
    },

    getJiraInstanceEntities(
      installationId: IntegrationInstallation['id']
    ): JiraInstanceEntitiesValue | undefined {
      const existing = installationId
        ? this._jiraInstanceEntities[installationId]
        : undefined;
      return existing;
    },

    _setJiraInstanceEntities(
      installationId: IntegrationInstallation['id'],
      entities: Partial<JiraInstanceEntitiesValue>
    ) {
      const existing = this._jiraInstanceEntities[installationId];
      const next: JiraInstanceEntitiesValue = existing
        ? {
            ...existing,
            ...entities,
          }
        : {
            ...this._getJiraInstanceEntitiesWithDefaults(installationId),
            ...entities,
          };
      this._jiraInstanceEntities[installationId] = next;
    },

    /** returns a fresh copy each time so the caller can mutate and use it with _setADOInstanceEntities */
    _getADOInstanceEntitiesWithDefaults(
      installationId?: IntegrationInstallation['id']
    ): ADOInstanceEntitiesValue {
      const existing = installationId
        ? this._adoInstanceEntities[installationId]
        : undefined;

      const defaults: ADOInstanceEntitiesValue = {
        adoBaseURL: '',
        adoProjects: [],
        adoTeams: [],
        adoProjectProcessConfig: new Map(),
        adoProjectWorkTypes: new Map(),
        adoProjectTags: new Map(),
        adoProjectFields: new Map(),
        adoProjectWorkTypeFieldMap: new Map(),
        // adoProjectBoards: new Map(),

        ...(existing ?? {}),
      };

      return cloneDeep(defaults);
    },

    getADOInstanceEntities(
      installationId: IntegrationInstallation['id']
    ): ADOInstanceEntitiesValue | undefined {
      const existing = installationId
        ? this._adoInstanceEntities[installationId]
        : undefined;
      return existing;
    },

    _setADOInstanceEntities(
      installationId: IntegrationInstallation['id'],
      entities: Partial<ADOInstanceEntitiesValue>
    ) {
      const existing = this._adoInstanceEntities[installationId];
      const next: ADOInstanceEntitiesValue = existing
        ? {
            ...existing,
            ...entities,
          }
        : {
            ...this._getADOInstanceEntitiesWithDefaults(installationId),
            ...entities,
          };
      this._adoInstanceEntities[installationId] = next;
    },

    // #region Credentials, accounts, installations

    async getIntegrationAuthorizeUrl({
      provider,
      name,
    }: {
      provider: IntegrationProvider;
      name: string;
    }) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const response = await IntegrationService.getIntegrationAuthorizeUrl({
          path: {
            organizationId: organization.id,
            provider: provider,
          },
          query: {
            name: name,
          },
        });
        return response.data.url;
      });
    },

    async loadIntegrationCredentials() {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const credentialsResponse =
          await IntegrationService.findIntegrationCredentials({
            path: {
              organizationId: organization.id,
            },
          });
        const credentials = credentialsResponse.data.data;
        this.integrationCredentials = credentials;
        return credentials;
      });
    },

    async updateIntegrationCredential({
      credentialId,
      data,
    }: {
      credentialId: IntegrationCredential['id'];
      data: UpdateIntegrationCredentialData['body'];
    }) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const credentialResponse =
          await IntegrationService.updateIntegrationCredential({
            path: {
              organizationId: organization.id,
              credentialsId: credentialId,
            },
            body: data,
          });
        await this.loadIntegrationCredentials(); // refresh credentials
        return credentialResponse.data;
      });
    },

    async deleteIntegrationCredential(
      data: SubmitDisableCredentialIntegrationJob['input']
    ) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        await JobsService.submitDisableCredentialIntegrationJob({
          path: {
            organizationId: organization.id,
          },
          body: {
            input: data,
          },
        });
        await this.loadIntegrationCredentials(); // refresh credentials
        return true;
      });
    },

    async loadIntegrationInstallations() {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const installations =
          await IntegrationService.findIntegrationInstallations({
            path: {
              organizationId: organization.id,
            },
          });
        this.integrationInstallations = installations.data.data;
        return installations.data.data;
      });
    },

    async loadIntegrationCredentialAccounts(
      credentialId: IntegrationCredential['id']
    ) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const response = await IntegrationService.findCredentialsAccounts({
          path: {
            organizationId: organization.id,
            credentialsId: credentialId,
          },
        });
        this.integrationCredentialAccounts[credentialId] = response.data.data;

        return cloneDeep(response.data.data);
      });
    },

    async registerInstallation(data: CreateIntegrationInstallation) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const response = await IntegrationService.createIntegrationInstallation(
          {
            path: {
              organizationId: organization.id,
            },
            body: data,
          }
        );
        await this.loadIntegrationInstallations(); // refresh installations
        return response.data.data;
      });
    },

    async disconnectInstallation(
      data: SubmitDisableInstallationIntegrationJob['input']
    ) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        await JobsService.submitDisableInstallationIntegrationJob({
          path: {
            organizationId: organization.id,
          },
          body: {
            input: data,
          },
        });
        await this.loadIntegrationInstallations(); // refresh installations
        return true;
      });
    },

    // #endregion Credentials, accounts, installations

    // #region Jira

    async loadJiraEntities(installationId: IntegrationInstallation['id']) {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      const DEFAULT_REQUEST_OPTIONS = {
        method: 'GET',
        body: undefined,
        headers: undefined,
      } as const;

      const pJiraProjects = im.proxy<JiraProject[]>(
        organization.id,
        installationId,
        '/rest/api/3/project?expand=issueTypes',
        DEFAULT_REQUEST_OPTIONS
      );

      const pJiraIssueTypes = im.proxy<JiraIssueType[]>(
        organization.id,
        installationId,
        '/rest/api/3/issuetype',
        DEFAULT_REQUEST_OPTIONS
      );

      const pJiraLabels = im.proxy<JiraPaginatedResponse<JiraLabel>>(
        organization.id,
        installationId,
        '/rest/api/3/label',
        DEFAULT_REQUEST_OPTIONS
      );

      // const pJiraFields = im.proxy<JiraPaginatedResponse<JiraField>>(
      //   organization.id,
      //   installationId,
      //   '/rest/api/3/field/search?maxResults=999&type=custom',
      //   DEFAULT_REQUEST_OPTIONS
      // );

      const pJiraPrioritySchemes = im.proxy<
        JiraPaginatedResponse<JiraPriorityScheme>
      >(
        organization.id,
        installationId,
        '/rest/api/3/priorityscheme?expand=projects,priorities',
        DEFAULT_REQUEST_OPTIONS
      );

      const pJiraUsers = im.proxy<JiraUser[]>(
        organization.id,
        installationId,
        '/rest/api/3/users/search?maxResults=9999',
        DEFAULT_REQUEST_OPTIONS
      );

      try {
        const [
          jiraProjects,
          jiraIssueTypes,
          jiraLabelsResponse,
          // jiraFieldsResponse,
          jiraPrioritySchemes,
          jiraUsers,
        ] = await Promise.all([
          pJiraProjects,
          pJiraIssueTypes,
          pJiraLabels,
          // pJiraFields,
          pJiraPrioritySchemes,
          pJiraUsers,
        ]);

        this._setJiraInstanceEntities(installationId, {
          jiraProjects: jiraProjects.output || [],
          jiraIssueTypes: jiraIssueTypes.output || [],
          jiraLabels: jiraLabelsResponse.output?.values || [],
          // jiraFields: jiraFieldsResponse.output?.values || [],
          jiraPrioritySchemes: jiraPrioritySchemes.output?.values || [],
          jiraUsers: jiraUsers.output || [],
        });
      } catch (error) {
        console.error(error);
        throw new Error('Failed to load Jira entities');
      }
    },

    async loadJiraEntitiesForAllInstallations() {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const installationIds: IntegrationInstallation['id'][] =
        this.connectedJiraInstances.map((installation) => installation.id);

      await Promise.all(
        installationIds.map((installationId) =>
          this.loadJiraEntities(installationId)
        )
      );
    },

    async loadJiraFieldsForProjectIssueType({
      installationId,
      projectId,
      issueTypeId,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: JiraProject['id'];
      issueTypeId: JiraIssueType['id'];
    }) {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      const DEFAULT_REQUEST_OPTIONS = {
        method: 'GET',
        body: undefined,
        headers: undefined,
      } as const;

      try {
        const jiraFieldsResponse = await im.proxy<
          Omit<JiraPaginatedResponse<unknown>, 'values'> & {
            fields: JiraFieldCreateMetadata[];
          }
        >(
          organization.id,
          installationId,
          `/rest/api/3/issue/createmeta/${projectId}/issuetypes/${issueTypeId}?maxResults=200`,
          DEFAULT_REQUEST_OPTIONS
        );

        const entitiesCopy =
          this._getJiraInstanceEntitiesWithDefaults(installationId);

        const jiraFields = jiraFieldsResponse.output?.fields || [];
        const projectIssueTypeFieldMap =
          entitiesCopy.jiraProjectIssueTypeFieldMap.get(projectId);
        if (!projectIssueTypeFieldMap) {
          entitiesCopy.jiraProjectIssueTypeFieldMap.set(
            projectId,
            new Map([[issueTypeId, jiraFields]])
          );
        } else {
          projectIssueTypeFieldMap.set(issueTypeId, jiraFields);
        }

        this._setJiraInstanceEntities(installationId, entitiesCopy);
      } catch (error) {
        console.error(error);
        throw new Error('Failed to load Jira fields');
      }
    },

    async loadJiraStatusesForProjectIssueTypes({
      installationId,
      projectId,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: JiraProject['id'];
    }) {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      const entitiesCopy =
        this._getJiraInstanceEntitiesWithDefaults(installationId);

      const DEFAULT_REQUEST_OPTIONS = {
        method: 'GET',
        body: undefined,
        headers: undefined,
      } as const;

      try {
        const issueTypeStatusResponse = await im.proxy<
          JiraIssueTypeWithStatus[]
        >(
          organization.id,
          installationId,
          `/rest/api/3/project/${projectId}/statuses`,
          DEFAULT_REQUEST_OPTIONS
        );

        if (issueTypeStatusResponse.output) {
          entitiesCopy.jiraProjectIssueTypeStatusMap.set(
            projectId,
            issueTypeStatusResponse.output
          );
        }

        this._setJiraInstanceEntities(installationId, entitiesCopy);
      } catch (error) {
        console.error(error);
        throw new Error('Failed to load Jira board config');
      }
    },

    /**
     * loads ALL entities for a project and all its issue types (unless `issueTypeIds` is provided).
     * When not passing `issueTypeIds`, all issue types will be loaded. Use with caution.
     *
     * Subsequent calls do not reload entities if already loaded.
     */
    async loadJiraFullEntitiesForProject({
      installationId,
      projectId,
      issueTypeIds,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: JiraProject['id'];
      /** if not provided, all issue types will be loaded */
      issueTypeIds?: JiraIssueType['id'][];
    }) {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const entities = this.getJiraInstanceEntities(installationId);
      if (!entities) throw new Error('No primary entities loaded');

      const promises: Promise<unknown>[] = [];

      // check if loadJiraStatusesForProjectIssueTypes needs to be called
      if (!entities.jiraProjectIssueTypeStatusMap.has(projectId)) {
        promises.push(
          this.loadJiraStatusesForProjectIssueTypes({
            installationId,
            projectId,
          })
        );
      }

      const { jiraIssueTypes } = useJiraProjectEntities({
        installationId,
        projectId,
      });

      // for each issue type, load fields if not already loaded
      jiraIssueTypes.value.forEach((issueType) => {
        if (issueTypeIds && !issueTypeIds.includes(issueType.id)) return;
        if (
          !entities.jiraProjectIssueTypeFieldMap
            .get(projectId)
            ?.has(issueType.id)
        ) {
          promises.push(
            this.loadJiraFieldsForProjectIssueType({
              installationId,
              projectId,
              issueTypeId: issueType.id,
            })
          );
        }
      });

      try {
        await Promise.all(promises);
        return true;
      } catch (error) {
        console.error(error);
      }

      return false;
    },

    async validateJQL(
      installationId: IntegrationInstallation['id'],
      jql: string
    ) {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      try {
        const jiraResponse = await im.proxy<{
          total: number;
          warningMessages: string[];
        }>(organization.id, installationId, `/rest/api/3/search`, {
          method: 'POST',
          headers: undefined,
          body: {
            jql,
            maxResults: 1,
            validateQuery: 'warn',
          },
        });
        return jiraResponse;
      } catch (error) {
        console.error(error);
        throw new Error('Failed to validate JQL');
      }
    },

    async loadJiraMappings() {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const installationIds: IntegrationInstallation['id'][] =
        this.connectedJiraInstances.map((installation) => installation.id);

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const response = await IntegrationService.findIntegrationInstallations({
          path: {
            organizationId: organization.id,
          },
        });
        const installations = response.data.data;

        const jiraInstallationConfigs = installations.filter(
          (config: { id: string }) => installationIds.includes(config.id)
        );
        this.jiraMappings = {};
        jiraInstallationConfigs.forEach(
          (config: { provider: string; id: string; config?: unknown }) => {
            if (config.provider !== IntegrationProvider.JIRA) {
              throw new Error(
                'Invalid installation config. You probably passed the wrong installation ID'
              );
            }
            this.jiraMappings[config.id] = config.config as JiraMappings;
          }
        );

        return cloneDeep(this.jiraMappings);
      });
    },

    async saveJiraMappings({
      installationId,
      mappings,
    }: {
      installationId: IntegrationInstallation['id'];
      mappings: JiraMappings;
    }) {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      const response = await IntegrationService.saveProviderConfiguration({
        path: {
          organizationId: organization.id,
          installationId: installationId,
        },
        body: mappings,
      });
      const jiraMappings = response.data as JiraMappings;
      this.jiraMappings[installationId] = jiraMappings;
      return cloneDeep(jiraMappings);
    },

    async initiateJiraImport({
      installationId,
      projectId,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: JiraProject['id'];
    }) {
      if (!this.isJiraConnected)
        throw new Error('Jira is not connected. Do not call this method');

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        await JobsService.submitIntegrationJob({
          path: {
            organizationId: organization.id,
          },
          body: {
            input: {
              installationId,
              projectId,
              type: IntegrationJobType.JIRA_IMPORT,
            },
          },
        });
        return true;
      });
    },

    async loadJiraJobHistory({
      loadType,
    }: {
      loadType: 'initial' | 'more' | 'refresh';
    }) {
      if (this.jiraJobHistory.isLoading) return;

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      const JIRA_JOBS: ReadonlyArray<IntegrationJobType> = [
        IntegrationJobType.JIRA_IMPORT,
        IntegrationJobType.JIRA_DISABLE_PROJECT,
        IntegrationJobType.JIRA_DISABLE_CREDENTIAL,
        IntegrationJobType.JIRA_DISABLE_INSTALLATION,
      ];

      const filterJiraJobs = (jobs: Job[]) => {
        return jobs.filter((job) => {
          const input = job.input as SubmitIntegrationJob['input'] | undefined;
          return (
            job.type === JobType.INTEGRATION &&
            input?.type &&
            JIRA_JOBS.includes(input.type)
          );
        });
      };

      const mergeJobs = (existing: Job[], incoming: Job[]) => {
        const map = new Map<Job['id'], Job>();
        existing.forEach((job) => map.set(job.id, job));
        incoming.forEach((job) => map.set(job.id, job)); // add new and update existing
        return orderBy([...map.values()], (j) => new Date(j.dateAdded), 'desc');
      };

      try {
        const LIMIT = Number.MAX_SAFE_INTEGER;

        if (loadType === 'initial') {
          this.jiraJobHistory.isLoading = 'initial';
          const response = await JobsService.findJobs({
            path: {
              organizationId: organization.id,
            },
            query: {
              type: JobType.INTEGRATION,
              limit: LIMIT,
            },
          });
          if (!response.data) return;
          const res = response.data;
          this.jiraJobHistory = {
            cursor: res.meta.cursor,
            hasMoreData: res.meta.hasMoreData,
            isLoading: false,
            jobs: filterJiraJobs(res.data),
            lastUpdated: new Date(),
          };
        } else if (loadType === 'more') {
          this.jiraJobHistory.isLoading = 'more';
          if (!this.jiraJobHistory.hasMoreData) return;
          const response = await JobsService.findJobs({
            path: {
              organizationId: organization.id,
            },
            query: {
              type: JobType.INTEGRATION,
              cursor: this.jiraJobHistory.cursor,
              limit: LIMIT,
            },
          });
          if (!response.data) return;
          const res = response.data;
          this.jiraJobHistory = {
            ...this.jiraJobHistory,
            cursor: res.meta.cursor,
            hasMoreData: res.meta.hasMoreData,
            jobs: filterJiraJobs(mergeJobs(this.jiraJobHistory.jobs, res.data)),
          };
        } else if (loadType === 'refresh') {
          this.jiraJobHistory.isLoading = 'refresh';
          const response = await JobsService.findJobs({
            path: {
              organizationId: organization.id,
            },
            query: {
              type: JobType.INTEGRATION,
              limit: LIMIT,
            },
          });
          if (!response.data) return;
          const res = response.data;
          this.jiraJobHistory.jobs = filterJiraJobs(
            mergeJobs(this.jiraJobHistory.jobs, res.data)
          );
        }
      } catch (err) {
        console.error(err);
        window.alert('Failed to load job history');
      } finally {
        this.jiraJobHistory.isLoading = false;
      }
    },

    // #endregion Jira

    // -------------------------------------------------- //

    // #region Azure DevOps

    async prepareADOBaseURL(installationId: IntegrationInstallation['id']) {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      const DEFAULT_REQUEST_OPTIONS = {
        method: 'GET',
        body: undefined,
        headers: undefined,
      } as const;

      const projects = await im.proxy<ADOListResponse<ADOTeamProject>>(
        organization.id,
        installationId,
        '_apis/projects?api-version=7.1-preview.4',
        DEFAULT_REQUEST_OPTIONS
      );
      if (!projects.output?.value.length)
        throw new Error('Failed to prepare Azure DevOps base URL');

      const projectUrl = projects.output.value[0].url;
      const baseURL = projectUrl?.split('/_apis')[0];
      if (!baseURL) throw new Error('Failed to prepare Azure DevOps base URL');
      this._setADOInstanceEntities(installationId, { adoBaseURL: baseURL });
    },

    async prepareAllADOBaseURLs() {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const installationIds: IntegrationInstallation['id'][] =
        this.connectedADOInstances.map((installation) => installation.id);

      await Promise.all(
        installationIds.map((installationId) =>
          this.prepareADOBaseURL(installationId)
        )
      );
    },

    async loadADOEntities(installationId: IntegrationInstallation['id']) {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      const DEFAULT_REQUEST_OPTIONS = {
        method: 'GET',
        body: undefined,
        headers: undefined,
      } as const;

      try {
        const pADOBaseURL = this.prepareADOBaseURL(installationId);

        const pProjects = im.proxy<ADOListResponse<ADOTeamProject>>(
          organization.id,
          installationId,
          '_apis/projects?api-version=7.1-preview.4',
          DEFAULT_REQUEST_OPTIONS
        );
        const pTeams = im.proxy<ADOListResponse<ADOTeam>>(
          organization.id,
          installationId,
          '_apis/teams?api-version=7.1-preview.3',
          DEFAULT_REQUEST_OPTIONS
        );

        const [projectsResponse, teamsResponse] = await Promise.all([
          pProjects,
          pTeams,
          pADOBaseURL,
        ]);

        const projects = projectsResponse?.output?.value || [];
        const teams = teamsResponse?.output?.value || [];

        this._setADOInstanceEntities(installationId, {
          adoProjects: projects,
          adoTeams: teams,
        });
      } catch (error) {
        console.error(error);
        throw new Error('Failed to load Azure DevOps entities');
      }
    },

    async loadADOEntitiesForAllInstallations() {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const installationIds: IntegrationInstallation['id'][] =
        this.connectedADOInstances.map((installation) => installation.id);

      await Promise.all(
        installationIds.map((installationId) =>
          this.loadADOEntities(installationId)
        )
      );
    },

    async loadADOProjectEntities({
      installationId,
      projectId,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: ADOTeamProject['id'];
    }) {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );
      const entities = this.getADOInstanceEntities(installationId);
      if (!entities?.adoBaseURL)
        throw new Error(
          'Azure DevOps base URL not prepared. Call prepareADOBaseURL first'
        );

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      const adoBaseURL = entities.adoBaseURL;
      const DEFAULT_REQUEST_OPTIONS = {
        method: 'GET',
        body: undefined,
        headers: undefined,
      } as const;

      try {
        const pProcessConfig = im.proxy<ADOProcessConfiguration>(
          organization.id,
          installationId,
          `${adoBaseURL}/${projectId}/_apis/work/processconfiguration?api-version=7.2-preview.1`,
          DEFAULT_REQUEST_OPTIONS
        );

        const pWorkTypes = im.proxy<ADOListResponse<ADOWorkType>>(
          organization.id,
          installationId,
          `${adoBaseURL}/${projectId}/_apis/wit/workitemtypes?api-version=7.1-preview.2`,
          DEFAULT_REQUEST_OPTIONS
        );

        const pTags = im.proxy<ADOListResponse<ADOWorkItemTag>>(
          organization.id,
          installationId,
          `${adoBaseURL}/${projectId}/_apis/wit/tags?api-version=7.1-preview.1`,
          DEFAULT_REQUEST_OPTIONS
        );

        const pFields = im.proxy<ADOListResponse<ADOWorkItemField2>>(
          organization.id,
          installationId,
          `${adoBaseURL}/${projectId}/_apis/wit/fields?api-version=7.1-preview.3`,
          DEFAULT_REQUEST_OPTIONS
        );

        // const teams = entities.adoTeams.filter(
        //   (team) => team.projectId === projectId
        // );

        // const pBoards = (async () => {
        //   const partialBoards = await Promise.all(
        //     teams.flatMap((team) =>
        //       im.proxy<
        //         ADOListResponse<Pick<ADOWorkBoard, 'id' | 'name' | 'url'>>
        //       >(
        //         organization.id,
        //         installationId,
        //         `${adoBaseURL}/${projectId}/${team.id}/_apis/work/boards?api-version=7.1-preview.1`,
        //         DEFAULT_REQUEST_OPTIONS
        //       )
        //     )
        //   );

        //   const fullBoards = await Promise.all(
        //     partialBoards.flatMap((partialBoards) =>
        //       (partialBoards.output?.value || []).map((partialBoard) =>
        //         im.proxy<ADOWorkBoard>(
        //           organization.id,
        //           installationId,
        //           partialBoard.url,
        //           DEFAULT_REQUEST_OPTIONS
        //         )
        //       )
        //     )
        //   );

        //   return fullBoards.filter((board): board is ADOWorkBoard => !!board);
        // })();

        const [
          processConfig,
          workTypesResponse,
          tagsResponse,
          fieldsResponse,
          // boards,
        ] = await Promise.all([
          pProcessConfig,
          pWorkTypes,
          pTags,
          pFields,
          // pBoards,
        ]);

        const entitiesCopy =
          this._getADOInstanceEntitiesWithDefaults(installationId);

        if (processConfig.output)
          entitiesCopy.adoProjectProcessConfig.set(
            projectId,
            processConfig.output
          );
        entitiesCopy.adoProjectWorkTypes.set(
          projectId,
          workTypesResponse?.output?.value || []
        );
        entitiesCopy.adoProjectTags.set(
          projectId,
          tagsResponse?.output?.value || []
        );
        entitiesCopy.adoProjectFields.set(
          projectId,
          fieldsResponse?.output?.value || []
        );
        // entitiesCopy.adoProjectBoards.set(projectId, boards);

        this._setADOInstanceEntities(installationId, entitiesCopy);
      } catch (error) {
        console.error(error);
        throw new Error('Failed to load Azure DevOps project entities');
      }
    },

    async loadADOFieldsForWorkType({
      installationId,
      projectId,
      workTypeName,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: ADOTeamProject['id'];
      workTypeName: ADOWorkType['name'];
    }) {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );
      const entities = this.getADOInstanceEntities(installationId);
      if (!entities?.adoBaseURL)
        throw new Error(
          'Azure DevOps base URL not prepared. Call prepareADOBaseURL first'
        );

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');
      const im = new IntegrationsModule();

      const adoBaseURL = entities.adoBaseURL;
      const DEFAULT_REQUEST_OPTIONS = {
        method: 'GET',
        body: undefined,
        headers: undefined,
      } as const;

      try {
        const pFields = im.proxy<ADOListResponse<ADOWorkItemTypeFieldInstance>>(
          organization.id,
          installationId,
          `${adoBaseURL}/${projectId}/_apis/wit/workitemtypes/${workTypeName}/fields?$expand=all&api-version=7.1-preview.3`,
          DEFAULT_REQUEST_OPTIONS
        );

        const fieldsResponse = await pFields;
        if (!fieldsResponse)
          throw new Error('Failed to load Azure DevOps fields');

        const entitiesCopy =
          this._getADOInstanceEntitiesWithDefaults(installationId);

        const adoFields = fieldsResponse.output?.value || [];
        const projectWorkTypeFieldMap =
          entitiesCopy.adoProjectWorkTypeFieldMap.get(projectId);
        if (!projectWorkTypeFieldMap) {
          entitiesCopy.adoProjectWorkTypeFieldMap.set(
            projectId,
            new Map([[workTypeName, adoFields]])
          );
        } else {
          projectWorkTypeFieldMap.set(workTypeName, adoFields);
        }

        this._setADOInstanceEntities(installationId, entitiesCopy);
      } catch (error) {
        console.error(error);
        throw new Error('Failed to load Azure DevOps fields');
      }
    },

    /**
     * loads ALL entities for a project and all its work types (unless `workTypeNames` is provided).
     * When not passing `workTypeNames`, all work types will be loaded. Use with caution.
     *
     * Subsequent calls do not reload entities if already loaded.
     */
    async loadADOFullEntitiesForProject({
      installationId,
      projectId,
      workTypeNames,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: ADOTeamProject['id'];
      /** if not provided, all work types will be loaded */
      workTypeNames?: ADOWorkType['name'][];
    }) {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const entities = this.getADOInstanceEntities(installationId);
      if (!entities?.adoBaseURL)
        throw new Error(
          'Azure DevOps base URL not prepared. Call prepareADOBaseURL first'
        );

      // load project entities if not already loaded
      if (!entities.adoProjectProcessConfig.has(projectId)) {
        await this.loadADOProjectEntities({
          installationId,
          projectId,
        });
      }

      const promises: Promise<unknown>[] = [];
      // for each work type, load fields if not already loaded
      entities.adoProjectWorkTypes.get(projectId)?.forEach((workType) => {
        if (workTypeNames && !workTypeNames.includes(workType.name)) return;
        if (
          !entities.adoProjectWorkTypeFieldMap
            .get(projectId)
            ?.has(workType.name)
        ) {
          promises.push(
            this.loadADOFieldsForWorkType({
              installationId,
              projectId,
              workTypeName: workType.name,
            })
          );
        }
      });

      try {
        await Promise.all(promises);
        return true;
      } catch (error) {
        console.error(error);
      }

      return false;
    },

    async loadADOMappings() {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const installationIds: string[] = this.connectedADOInstances.map(
        (installation) => installation.id
      );

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        const response = await IntegrationService.findIntegrationInstallations({
          path: {
            organizationId: organization.id,
          },
        });
        const installationConfigs = response.data.data;

        const adoInstallationConfigs = installationConfigs.filter(
          (config: { id: string }) => installationIds.includes(config.id)
        );
        this.adoMappings = {};
        adoInstallationConfigs.forEach(
          (config: { provider: string; id: string; config?: unknown }) => {
            if (config.provider !== IntegrationProvider.ADO) {
              throw new Error(
                'Invalid installation config. You probably passed the wrong installation ID'
              );
            }

            this.adoMappings[config.id] = config.config as AdoMappings;
          }
        );

        return cloneDeep(this.adoMappings);
      });
    },

    async saveADOMappings({
      installationId,
      mappings,
    }: {
      installationId: IntegrationInstallation['id'];
      mappings: AdoMappings;
    }) {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      const response = await IntegrationService.saveProviderConfiguration({
        path: {
          organizationId: organization.id,
          installationId: installationId,
        },
        body: mappings,
      });

      const adoMappings = response.data as AdoMappings;

      this.adoMappings[installationId] = adoMappings;
      return cloneDeep(adoMappings);
    },

    async initiateADOImport({
      installationId,
      projectId,
    }: {
      installationId: IntegrationInstallation['id'];
      projectId: ADOTeamProject['id'];
    }) {
      if (!this.isADOConnected)
        throw new Error(
          'Azure DevOps is not connected. Do not call this method'
        );

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      return apiRequestHandler(async () => {
        await JobsService.submitIntegrationJob({
          path: {
            organizationId: organization.id,
          },
          body: {
            input: {
              installationId,
              projectId,
              type: IntegrationJobType.ADO_IMPORT,
            },
          },
        });
        return true;
      });
    },

    async loadADOJobHistory({
      loadType,
    }: {
      loadType: 'initial' | 'more' | 'refresh';
    }) {
      if (this.adoJobHistory.isLoading) return;

      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      const ADO_JOBS: ReadonlyArray<IntegrationJobType> = [
        IntegrationJobType.ADO_IMPORT,
        IntegrationJobType.ADO_DISABLE_PROJECT,
        IntegrationJobType.ADO_DISABLE_CREDENTIAL,
        IntegrationJobType.ADO_DISABLE_INSTALLATION,
      ];

      const filterADOJobs = (jobs: Job[]) => {
        return jobs.filter((job) => {
          const input = job.input as SubmitIntegrationJob['input'] | undefined;
          return (
            job.type === JobType.INTEGRATION &&
            input?.type &&
            ADO_JOBS.includes(input.type)
          );
        });
      };

      const mergeJobs = (existing: Job[], incoming: Job[]) => {
        const map = new Map<Job['id'], Job>();
        existing.forEach((job) => map.set(job.id, job));
        incoming.forEach((job) => map.set(job.id, job)); // add new and update existing
        return orderBy([...map.values()], (j) => new Date(j.dateAdded), 'desc');
      };

      try {
        const LIMIT = Number.MAX_SAFE_INTEGER;

        if (loadType === 'initial') {
          this.adoJobHistory.isLoading = 'initial';
          const response = await JobsService.findJobs({
            path: {
              organizationId: organization.id,
            },
            query: {
              type: JobType.INTEGRATION,
              limit: LIMIT,
            },
          });
          if (!response.data) return;
          const res = response.data;
          this.adoJobHistory = {
            cursor: res.meta.cursor,
            hasMoreData: res.meta.hasMoreData,
            isLoading: false,
            jobs: filterADOJobs(res.data),
            lastUpdated: new Date(),
          };
        } else if (loadType === 'more') {
          this.adoJobHistory.isLoading = 'more';
          if (!this.adoJobHistory.hasMoreData) return;
          const response = await JobsService.findJobs({
            path: {
              organizationId: organization.id,
            },
            query: {
              type: JobType.INTEGRATION,
              cursor: this.adoJobHistory.cursor,
              limit: LIMIT,
            },
          });
          if (!response.data) return;
          const res = response.data;
          this.adoJobHistory = {
            ...this.adoJobHistory,
            cursor: res.meta.cursor,
            hasMoreData: res.meta.hasMoreData,
            jobs: filterADOJobs(mergeJobs(this.adoJobHistory.jobs, res.data)),
          };
        } else if (loadType === 'refresh') {
          this.adoJobHistory.isLoading = 'refresh';
          const response = await JobsService.findJobs({
            path: {
              organizationId: organization.id,
            },
            query: {
              type: JobType.INTEGRATION,
              limit: LIMIT,
            },
          });
          if (!response.data) return;
          const res = response.data;
          this.adoJobHistory.jobs = filterADOJobs(
            mergeJobs(this.adoJobHistory.jobs, res.data)
          );
        }
      } catch (err) {
        console.error(err);
        window.alert('Failed to load job history');
      } finally {
        this.adoJobHistory.isLoading = false;
      }
    },

    // #endregion Azure DevOps

    // #region General

    async requestItemSync({
      installationId,
      installationProvider,
      integrationData,
    }: {
      installationId: IntegrationInstallation['id'];
      installationProvider: IntegrationInstallation['provider'];
      integrationData: WorkItemIntegrations;
    }) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) throw new Error('No active organization');

      if (installationProvider === IntegrationProvider.ADO) {
        return apiRequestHandler(async () => {
          if (!integrationData.ado) throw new Error('No ADO data');
          const response = await IntegrationService.syncAdoWorkitem({
            path: {
              organizationId: organization.id,
              installationId: installationId,
            },
            body: {
              adoWorkItemId: integrationData.ado.id,
              adoProjectId: integrationData.ado.project.id,
            },
          });
          if (!response.data) {
            return;
          }
          return true;
        });
      } else if (installationProvider === IntegrationProvider.JIRA) {
        return apiRequestHandler(async () => {
          if (!integrationData.jira) throw new Error('No Jira data');
          const response = await IntegrationService.syncJiraWorkitem({
            path: {
              organizationId: organization.id,
              installationId: installationId,
            },
            body: {
              jiraIssueId: integrationData.jira.id,
              jiraProjectId: integrationData.jira.project.id,
            },
          });
          if (!response.data) {
            return;
          }
          return true;
        });
      }
    },

    /** conditionally loads jira, ado and other mappings so they can be consumed globally */
    async loadApplicableMappings() {
      this.loadIntegrationInstallations();
      if (this.isJiraConnected) this.loadJiraMappings();
      if (this.isADOConnected) {
        this.loadADOMappings();
        this.prepareAllADOBaseURLs();
      }
    },

    // #endregion General
  },
});
